(function () {
  const STORAGE_KEY = 'suncoCompareProducts:v1';
  const settings = Object.assign(
    {
      maxProducts: 4,
      buttonLabel: 'Compare',
      addedLabel: 'Added',
      trayLabel: 'products selected',
    },
    window.SuncoCompareSettings || {}
  );
  settings.maxProducts = parseInt(settings.maxProducts, 10) || 4;

  const HIDDEN_SPEC_KEYS = new Set(['brand', 'equivalency']);
  const BASIC_SPEC_KEYS = new Set(['color_temp']);
  const SPEC_ROW_ORDER = [
    'model_number',
    'lumens',
    'wattage',
    'efficiency',
    'voltage',
    'output_voltage',
    'dimmable',
    'dimming_details',
    'tuning_details',
    'selectable_details',
    'cri',
    'beam_angle',
    'moisture_rating',
    'ip_rating',
    'ic_rated',
    'fire_rating',
    'bulb_base',
    'base_type',
    'trim_color',
    'trim_type',
    'components',
    'power_type',
    'mount_type',
    'motion_sensor',
    'sensor_type',
    'battery',
    'linkable',
    'max_linking',
    'dimensions',
    'finish',
    'housing',
    'material',
    'compliance',
    'certifications',
    'lifetime',
    'warranty',
  ];

  const state = {
    products: [],
    tray: null,
    modal: null,
    observer: null,
    updateFrame: null,
    alignFrame: null,
    activeTrigger: null,
  };

  const getFocusableElements = (container) =>
    [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);

  const trapFocus = (event, container) => {
    const focusable = getFocusableElements(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!container.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const isCompareTrayAllowedPage = () => {
    const template = document.querySelector('main[data-template]')?.dataset.template || '';
    const path = window.location.pathname || '';

    return (
      /^product(\.|$)/.test(template)
      || /^collection(\.|$)/.test(template)
      || path.includes('/products/')
      || /^\/collections\/[^/]+/.test(path)
    );
  };

  const getCompareObserverRoot = () =>
    document.querySelector('main[data-template^="product"], main[data-template^="collection"]');

  const isBlank = (value) => {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    const normalized = String(value).trim().toLowerCase();
    return normalized === '' || normalized === 'na' || normalized === 'n/a' || normalized === 'null';
  };

  const cleanValue = (value) => {
    if (isBlank(value)) return '';
    if (Array.isArray(value)) return value.filter((entry) => !isBlank(entry)).join(', ');
    if (typeof value === 'object') {
      if (!isBlank(value.value)) return cleanValue(value.value);
      if (!isBlank(value.label)) return cleanValue(value.label);
      return '';
    }
    return String(value).trim();
  };

  const normalizeSpecs = (specs) =>
    Array.isArray(specs)
      ? specs
          .map((spec) => ({
            key: spec.key,
            label: spec.label,
            value: cleanValue(spec.value),
          }))
          .filter((spec) => spec.key && spec.label && !HIDDEN_SPEC_KEYS.has(spec.key) && !isBlank(spec.value))
      : [];

  const normalizeProduct = (product) => {
    if (!product || !product.id) return null;
    return Object.assign({}, product, {
      id: String(product.id),
      specs: normalizeSpecs(product.specs),
    });
  };

  const readStorage = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(normalizeProduct).filter(Boolean).slice(0, settings.maxProducts) : [];
    } catch (error) {
      return [];
    }
  };

  const writeStorage = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.products.slice(0, settings.maxProducts)));
    } catch (error) {
      // Storage can be unavailable in strict privacy modes. The in-memory state still works for this visit.
    }
  };

  const parseProductData = (source) => {
    const script = source?.querySelector('[data-sunco-compare-product]');
    if (!script) return null;

    try {
      const data = JSON.parse(script.textContent || '{}');
      if (!data || !data.id) return null;
      data.id = String(data.id);
      data.specs = normalizeSpecs(data.specs);
      return hydrateFromRenderedCard(data, source);
    } catch (error) {
      return null;
    }
  };

  const hydrateFromRenderedCard = (product, source) => {
    const card = source.closest('[data-sunco-product-card]');
    if (!card) return product;

    const title = card.querySelector('[data-sunco-card-title]')?.textContent?.trim();
    const sku = card.querySelector('[data-sunco-card-sku]')?.textContent?.trim();
    const price = card.querySelector('[data-sunco-card-price]')?.textContent?.trim();
    const compareAtPrice = card.querySelector('[data-sunco-card-compare-price]')?.textContent?.trim();
    const image = card.querySelector('[data-sunco-card-image]');
    const url = card.querySelector('[data-sunco-card-link]')?.getAttribute('href');
    const variantId = card.dataset.variantId;

    return Object.assign({}, product, {
      title: title || product.title,
      sku: sku || product.sku,
      price: price || product.price,
      compareAtPrice: compareAtPrice || product.compareAtPrice,
      url: url || product.url,
      variantId: variantId || product.variantId,
      image: {
        src: image?.currentSrc || image?.src || product.image?.src || '',
        alt: image?.alt || product.image?.alt || product.title || '',
      },
    });
  };

  const findProductIndex = (id) => state.products.findIndex((product) => String(product.id) === String(id));

  const toggleProduct = (source) => {
    const product = parseProductData(source);
    if (!product) return;

    const index = findProductIndex(product.id);
    if (index >= 0) {
      state.products.splice(index, 1);
    } else if (state.products.length < settings.maxProducts) {
      state.products.push(product);
    }

    writeStorage();
    syncUi({ forceButtons: true });
  };

  const createTray = () => {
    const tray = document.createElement('div');
    tray.className = 'sunco-compare-tray';
    tray.setAttribute('data-sunco-compare-tray', '');
    tray.setAttribute('role', 'region');
    tray.setAttribute('aria-label', 'Product comparison');
    tray.innerHTML = `
      <div class="sunco-compare-tray__summary" data-sunco-compare-tray-summary role="status" aria-live="polite" aria-atomic="true"></div>
      <div class="sunco-compare-tray__actions">
        <button type="button" class="sunco-compare-tray__button sunco-compare-tray__button--primary" data-sunco-compare-open>Compare products</button>
        <button type="button" class="sunco-compare-tray__button sunco-compare-tray__button--secondary" data-sunco-compare-clear>Clear</button>
      </div>
    `;
    document.body.appendChild(tray);
    return tray;
  };

  const createModal = () => {
    const modal = document.createElement('div');
    modal.className = 'sunco-compare-modal';
    modal.setAttribute('data-sunco-compare-modal', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'sunco-compare-modal-title');
    modal.setAttribute('aria-describedby', 'sunco-compare-modal-intro sunco-compare-modal-mobile-hint');
    modal.hidden = true;
    modal.innerHTML = `
      <div class="sunco-compare-modal__panel" data-sunco-compare-panel>
        <div class="sunco-compare-modal__header">
          <div>
            <h2 class="sunco-compare-modal__title" id="sunco-compare-modal-title">Compare products</h2>
            <p class="sunco-compare-modal__intro" id="sunco-compare-modal-intro">Review product details and key specs side by side.</p>
            <p class="sunco-compare-modal__mobile-hint" id="sunco-compare-modal-mobile-hint">Swipe horizontally to compare products.</p>
          </div>
          <button type="button" class="sunco-compare-modal__close" data-sunco-compare-close>Close</button>
        </div>
        <div class="sunco-compare-modal__body" data-sunco-compare-table-wrap role="region" aria-label="Scrollable product comparison table" tabindex="-1"></div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('[data-sunco-compare-table-wrap]')?.addEventListener('scroll', (event) => {
      modal.classList.toggle('is-compare-header-stuck', event.currentTarget.scrollTop > 8);
    });
    return modal;
  };

  const getTray = () => {
    if (!state.tray) state.tray = createTray();
    return state.tray;
  };

  const getModal = () => {
    if (!state.modal) state.modal = createModal();
    return state.modal;
  };

  const syncButtons = () => {
    document.querySelectorAll('[data-sunco-compare-source]').forEach((source) => {
      const data = parseProductData(source);
      const button = source.querySelector('[data-sunco-compare-toggle]');
      const label = source.querySelector('[data-sunco-compare-label]');
      const accessibleLabel = source.querySelector('[data-sunco-compare-accessible-label]');
      if (!data || !button || !label || !accessibleLabel) return;

      const selected = findProductIndex(data.id) >= 0;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.disabled = !selected && state.products.length >= settings.maxProducts;
      label.textContent = selected ? settings.addedLabel : settings.buttonLabel;
      accessibleLabel.textContent = selected
        ? `Remove ${data.title} from comparison`
        : `${settings.buttonLabel} ${data.title}`;
    });
  };

  const syncTray = () => {
    if (!isCompareTrayAllowedPage()) {
      if (state.tray) state.tray.classList.remove('is-visible');
      return;
    }

    const count = state.products.length;

    if (!count) {
      if (state.tray) state.tray.classList.remove('is-visible');
      return;
    }

    const tray = getTray();
    const summary = tray.querySelector('[data-sunco-compare-tray-summary]');

    tray.classList.toggle('is-visible', count > 0);
    if (summary) {
      const plural = count === 1 ? 'product selected' : settings.trayLabel;
      summary.textContent = `${count} ${plural}`;
    }
  };

  const syncUi = ({ forceButtons = false } = {}) => {
    if (forceButtons || state.products.length > 0) syncButtons();
    syncTray();
    if (state.modal?.classList.contains('is-open')) renderModalTable();
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const productHeader = (product) => `
    <div class="sunco-compare-product">
      ${
        product.image?.src
          ? `<img class="sunco-compare-product__image" src="${escapeHtml(product.image.src)}" alt="${escapeHtml(product.image.alt || product.title)}" loading="lazy">`
          : ''
      }
      <a class="sunco-compare-product__title" href="${escapeHtml(product.url || '#')}">${escapeHtml(product.title)}</a>
      ${product.price ? `<span class="sunco-compare-product__price"><strong>${escapeHtml(product.price)}</strong></span>` : ''}
      <a class="sunco-compare-product__learn-more" href="${escapeHtml(product.url || '#')}">Learn More</a>
      <button type="button" class="sunco-compare-product__remove-text" data-sunco-compare-remove="${escapeHtml(product.id)}" aria-label="Remove ${escapeHtml(product.title)} from compare">X Remove</button>
    </div>
  `;

  const syncProductHeaderAlignment = (modal) => {
    if (!modal) return;
    const products = [...modal.querySelectorAll('.sunco-compare-product')];
    products.forEach((product) => product.style.removeProperty('--sunco-compare-title-min-height'));

    const titleHeights = products
      .map((product) => product.querySelector('.sunco-compare-product__title')?.getBoundingClientRect().height || 0)
      .filter(Boolean);
    const maxTitleHeight = Math.ceil(Math.max(0, ...titleHeights));

    if (maxTitleHeight > 0) {
      products.forEach((product) => {
        product.style.setProperty('--sunco-compare-title-min-height', `${maxTitleHeight}px`);
      });
    }
  };

  const scheduleProductHeaderAlignment = () => {
    if (!state.modal?.classList.contains('is-open') || state.alignFrame) return;
    state.alignFrame = window.requestAnimationFrame(() => {
      state.alignFrame = null;
      syncProductHeaderAlignment(state.modal);
    });
  };

  const buildSpecRows = () => {
    const rows = [];
    const seen = new Set();
    const orderLookup = new Map(SPEC_ROW_ORDER.map((key, index) => [key, index]));

    state.products.forEach((product) => {
      (product.specs || []).forEach((spec) => {
        if (!seen.has(spec.key) && !HIDDEN_SPEC_KEYS.has(spec.key) && !BASIC_SPEC_KEYS.has(spec.key)) {
          seen.add(spec.key);
          rows.push({ key: spec.key, label: spec.label, order: rows.length });
        }
      });
    });

    return rows
      .sort((a, b) => {
        const aOrder = orderLookup.has(a.key) ? orderLookup.get(a.key) : Number.MAX_SAFE_INTEGER;
        const bOrder = orderLookup.has(b.key) ? orderLookup.get(b.key) : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder || a.order - b.order;
      })
      .map(({ key, label }) => ({ key, label }));
  };

  const specValue = (product, key) => {
    const spec = (product.specs || []).find((item) => item.key === key);
    return spec && !isBlank(spec.value) ? spec.value : '';
  };

  const renderModalTable = () => {
    const modal = getModal();
    const body = modal.querySelector('[data-sunco-compare-table-wrap]');
    if (!body) return;

    if (!state.products.length) {
      body.innerHTML = '<p class="sunco-compare-empty">Select products to compare.</p>';
      return;
    }

    const productColumns = state.products.map((product) => `<th scope="col">${productHeader(product)}</th>`).join('');
    const specRows = buildSpecRows();
    const basicRows = [
      ['SKU', (product) => product.sku],
      ['Variant', (product) => product.variantTitle && product.variantTitle !== 'Default Title' ? product.variantTitle : ''],
      ['Color Temperature', (product) => specValue(product, 'color_temp'), { requireValue: true }],
    ].filter(([, resolver, options]) => !options?.requireValue || state.products.some((product) => !isBlank(resolver(product))));

    const rowMarkup = [
      ...basicRows.map(
        ([label, resolver]) => `
          <tr>
            <th scope="row">${escapeHtml(label)}</th>
            ${state.products
              .map((product) => `<td>${escapeHtml(cleanValue(resolver(product))) || '<span class="sunco-compare-empty">-</span>'}</td>`)
              .join('')}
          </tr>
        `
      ),
      ...specRows.map(
        (row) => `
          <tr>
            <th scope="row">${escapeHtml(row.label)}</th>
            ${state.products
              .map((product) => `<td>${escapeHtml(specValue(product, row.key)) || '<span class="sunco-compare-empty">-</span>'}</td>`)
              .join('')}
          </tr>
        `
      ),
    ].join('');

    body.innerHTML = `
      <table class="sunco-compare-table">
        <caption class="visually-hidden">Product comparison details</caption>
        <thead>
          <tr>
            <th scope="col">Product</th>
            ${productColumns}
          </tr>
        </thead>
        <tbody>${rowMarkup}</tbody>
      </table>
    `;
    body.scrollTop = 0;
    modal.classList.remove('is-compare-header-stuck');
    requestAnimationFrame(() => {
      syncProductHeaderAlignment(modal);
      body.tabIndex = body.scrollWidth > body.clientWidth ? 0 : -1;
    });
  };

  const openModal = (trigger) => {
    const modal = getModal();
    state.activeTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    modal.hidden = false;
    renderModalTable();
    modal.classList.add('is-open');
    document.body.classList.add('sunco-compare-modal-open');
    modal.querySelector('[data-sunco-compare-close]')?.focus();
  };

  const closeModal = ({ restoreFocus = true } = {}) => {
    if (!state.modal) return;
    state.modal.classList.remove('is-open');
    state.modal.hidden = true;
    document.body.classList.remove('sunco-compare-modal-open');
    if (restoreFocus && state.activeTrigger instanceof HTMLElement && state.activeTrigger.isConnected) {
      state.activeTrigger.focus({ preventScroll: true });
    }
    state.activeTrigger = null;
  };

  const clearProducts = () => {
    state.products = [];
    writeStorage();
    syncUi({ forceButtons: true });
  };

  const removeProduct = (id) => {
    state.products = state.products.filter((product) => String(product.id) !== String(id));
    writeStorage();
    syncUi({ forceButtons: true });
    if (state.modal?.classList.contains('is-open')) {
      state.modal.querySelector('[data-sunco-compare-close]')?.focus();
    }
  };

  const scheduleSync = () => {
    if (!state.products.length) return;
    if (state.updateFrame) return;
    state.updateFrame = window.requestAnimationFrame(() => {
      state.updateFrame = null;
      syncButtons();
    });
  };

  const bindEvents = () => {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-sunco-compare-toggle]');
      if (toggle) {
        event.preventDefault();
        toggleProduct(toggle.closest('[data-sunco-compare-source]'));
        return;
      }

      const openButton = event.target.closest('[data-sunco-compare-open]');
      if (openButton) {
        event.preventDefault();
        openModal(openButton);
        return;
      }

      if (event.target.closest('[data-sunco-compare-clear]')) {
        event.preventDefault();
        clearProducts();
        return;
      }

      if (event.target.closest('[data-sunco-compare-close]')) {
        event.preventDefault();
        closeModal();
        return;
      }

      const remove = event.target.closest('[data-sunco-compare-remove]');
      if (remove) {
        event.preventDefault();
        removeProduct(remove.getAttribute('data-sunco-compare-remove'));
        return;
      }

      const modal = event.target.closest('[data-sunco-compare-modal]');
      if (modal && !event.target.closest('[data-sunco-compare-panel]')) closeModal();
    });

    document.addEventListener('keydown', (event) => {
      if (!state.modal?.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        closeModal();
      } else if (event.key === 'Tab') {
        trapFocus(event, state.modal);
      }
    });

    window.addEventListener('resize', scheduleProductHeaderAlignment);

    ['shopify:section:load', 'shopify:section:select', 'shopify:block:select'].forEach((eventName) => {
      document.addEventListener(eventName, scheduleSync);
    });

    const observerRoot = getCompareObserverRoot();
    if (observerRoot) {
      state.observer = new MutationObserver((mutations) => {
        const hasRelevantChange = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some((node) => {
            if (!(node instanceof HTMLElement)) return false;
            return node.matches('[data-sunco-compare-source], [data-sunco-product-card]')
              || node.querySelector('[data-sunco-compare-source], [data-sunco-product-card]');
          })
        );

        if (hasRelevantChange) scheduleSync();
      });
      state.observer.observe(observerRoot, { childList: true, subtree: true });
    }
  };

  const init = () => {
    state.products = readStorage();
    bindEvents();
    syncUi();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
